import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ArrowRight, Mail, Phone, ShieldCheck } from 'lucide-react';
import { navLinks, site } from '../../data/site';
import Logo from './Logo';
import MobileMenu from './MobileMenu';
import cn from '../../lib/cn';

/**
 * Sticky header. Always solid with dark text, on every route and scroll
 * position — a transparent-over-hero variant was tried and dropped: the
 * header is `sticky`, which reserves its own space in the layout rather
 * than overlaying the hero beneath it, so "transparent" meant white text
 * over the plain page background instead of the dark hero. A solid header
 * has no such failure mode.
 */
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <>
      {/* Utility bar — hidden on mobile to save vertical space */}
      <div className="hidden bg-ink-950 text-brand-100/85 lg:block">
        <div className="container flex h-9 max-w-6xl items-center justify-between text-xs">
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-3.5 w-3.5 text-brand-400" />
          </span>
          <div className="flex items-center gap-6">
            <a href={`tel:${site.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 transition hover:text-white">
              <Phone className="h-3.5 w-3.5" />
              {site.phone}
            </a>
            <a href={`mailto:${site.email}`} className="flex items-center gap-2 transition hover:text-white">
              <Mail className="h-3.5 w-3.5" />
              {site.email}
            </a>
          </div>
        </div>
      </div>

      <header
        className={cn(
          'sticky top-0 z-50 border-b border-ink-100 bg-white/90 backdrop-blur-md transition-shadow duration-300 ease-smooth',
          scrolled && 'shadow-soft'
        )}
      >
        <div className="container flex h-[72px] max-w-6xl items-center justify-between gap-6">
          <Logo tone="light" />

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) =>
                  cn(
                    'relative rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-200',
                    isActive ? 'text-brand-700' : 'text-ink-600 hover:text-ink-900'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    <span
                      className={cn(
                        'absolute inset-x-3.5 -bottom-0.5 h-0.5 rounded-full bg-brand-600 transition-opacity duration-300',
                        isActive ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <NavLink
              to="/request-pickup"
              className="btn hidden bg-brand-600 px-5 py-2.5 text-sm text-white shadow-soft hover:bg-brand-700 hover:shadow-lift sm:inline-flex"
            >
              Request Pickup
              <ArrowRight className="h-4 w-4" />
            </NavLink>

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              className="grid h-11 w-11 place-items-center rounded-xl text-ink-800 transition hover:bg-ink-100 lg:hidden"
            >
              <span className="relative block h-4 w-5">
                <span className="absolute left-0 top-0 h-0.5 w-5 rounded bg-current" />
                <span className="absolute left-0 top-1/2 h-0.5 w-4 -translate-y-1/2 rounded bg-current" />
                <span className="absolute bottom-0 left-0 h-0.5 w-5 rounded bg-current" />
              </span>
            </button>
          </div>
        </div>
      </header>

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
